import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit } from '@angular/core';
import { InputValidation } from '../validations/input.validation';
import { ColorPickerDirective } from '../angular2-color-picker';
import { AppStateService } from '../services/app-state.service';
import { AccountColorsState, AccountColorsService } from '../services/account-colors.service';

const EMPTY_COLOR_IMAGE = '../assets/images/nocolor.jpg';

@Component({
  selector: 'color-editor',
  template: `
    <input class="bg-color-hex settings-value"
           type="text"
           #colorinput
           (change)="colorChanged(colorinput.value)"
           [value]="color"
           (paste)="onpaste($event)"
           (mousedown)="selectInputShowCursorInAllBrowsers($event)"
           [disabled]="disabled"
           (keyup.enter)="handleEnter($event)"
           [placeholder]="'COMMON.EDITORS.COLOR.NONE' | translate"/>
    <input class="bg-color-value"
           *ngIf="!disabled"
           type="button"
           [disabled]="disabled"
           [ngStyle]="colorPickerButtonStyle"
           [(colorPicker)]="color"
           [containerSelector]="containerSelector"
           [cpPosition]="'bottom'"
           (colorPickerChange)="colorChanged($event)"
           [cpAlphaChannel]="'disabled'"
           (cpToggleChange)="handleCpToggle($event)"
           [cpPresetColors]="customColors"
           (cpPresetColorsChanged)="handlePresetColorsChanged($event)"
    />`
})

export class ColorEditorComponent implements OnInit {
  @Input() disabled: boolean = false;
  @Input() color: string;
  @Input() presetColorsPersistenceKey?: string;
  @Input() containerSelector: string;
  @Output() onColorChanged: EventEmitter<any> = new EventEmitter();
  @Output() onDialogShowHide: EventEmitter<any> = new EventEmitter();
  @ViewChild('colorinput', { static: false }) colorinput: ElementRef;
  @ViewChild('colorPicker', { static: false }) colorPicker: ColorPickerDirective;
  scrollingContainerElement: Element;
  customColors: string[] = ['#FFFFFF', '#000000', '#2889e9', '#e920e9', '#fff500', '#90EE90'];

  get colorPickerButtonStyle() {
    return this.color ? { 'background': this.color } : { 'background': 'url(' + EMPTY_COLOR_IMAGE + ') no-repeat' };
  }

  constructor(private appStateService: AppStateService, private accountColorsService: AccountColorsService) {
  }

  ngOnInit(): void {
    // get the element that is scrollable and can effect the position of this floating panel
    if (this.containerSelector && this.containerSelector !== '') {
      this.scrollingContainerElement = document.querySelector(this.containerSelector);
    }

    this.accountColorsService.state$.subscribe((accountColorsState: AccountColorsState) => {
      if (accountColorsState.isLoadingColorPickerSettings) {
        return;
      }
      if (accountColorsState && accountColorsState.colors) {
        this.customColors = accountColorsState.colors.split(',');
      } else {
        this.accountColorsService.setColors(this.customColors.join(','));
      }
    });
  }

  onpaste(event: any) {
    const colorInput = this.colorinput.nativeElement;
    const currentColor = this.color;

    setTimeout(() => {
      const colorInputValue = colorInput.value;
      if (this.isColorValid(colorInputValue) === false) {
        colorInput.value = currentColor;
      } else {
        this.onColorChanged.next(colorInputValue);
      }
    }, 1);
  }

  changeSelectedColor(color: string) {
    if (this.isColorValid(color) === false) {
      this.colorinput.nativeElement.value = this.color;
    } else {
      this.onColorChanged.next(color);
    }
  }

  isColorValid(color: string): boolean {
    let isValid: boolean = false;
    if (color === '' || color === null) {
      isValid = true;
    } else {
      isValid = InputValidation.hexColorValidation((color));
      if (isValid === false) {
        isValid = InputValidation.rgbColorValidation((color));
      }
    }
    return isValid;
  }

  colorChanged(color: string) {
    this.changeSelectedColor(color);
  }

  handleEnter(event: any) {
    const color = event.target.value || null;
    this.changeSelectedColor(color);
  }

  closeColorPicker() {
    if (this.colorPicker) {
      this.colorPicker.closeDialog();
    }
  }

  handleCpToggle(opened: boolean) {
    if (this.onDialogShowHide) {
      this.onDialogShowHide.next(opened);
    }
  }

  selectInputShowCursorInAllBrowsers(event: any) {
    if (this.appStateService.isBrowserEdge() || this.appStateService.isBrowserIE()) {
      const target: HTMLInputElement = event.target || event.srcElement || event.currentTarget;
      target.select();
    }
  }

  handlePresetColorsChanged(event: any) {
    if (this.presetColorsPersistenceKey) {
      this.accountColorsService.setColors(event.join(','));
    }
  }
}
